import cn from "classnames";
import { CHART_DATE_FORMATS_FOR_PICKER, ChartRangeType } from "components";
import { t } from "i18next";
import { StatisticChartDatePicker } from "modules/dashboard";
import moment from "moment";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import { useGetProductLifetimeStats } from "../../apis";
import {
  ProductLifetimeChart,
  ProductStatsByBarcodeTable
} from "../../components";
import {
  getRangeParams,
  PRODUCT_STATS_RANGE_TABS,
  ProductStatsRange
} from "./productStatistics.helpers";

import styles from "./ProductStatistics.module.scss";

const ProductStatistics = () => {
  const { productId } = useParams();

  const [range, setRange] = useState<ProductStatsRange>(
    ProductStatsRange.Month
  );
  const [customRange, setCustomRange] = useState<[Date, Date]>([
    moment().subtract(1, "month").toDate(),
    new Date()
  ]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const params = getRangeParams(range, customRange);
  const breakdownBy = params.breakdownBy === "Month" ? "Month" : "Day";

  const { stats, isLoading } = useGetProductLifetimeStats(
    productId as string,
    params
  );

  const data = stats?.data ?? [];

  useEffect(() => {
    setSelectedIndex(data.length > 0 ? data.length - 1 : null);
  }, [stats]);

  const selectedPoint =
    selectedIndex !== null ? data[selectedIndex] ?? null : null;

  return (
    <div className={styles.productStatistics}>
      <div className={styles.productStatistics_header}>
        <div className={styles.productStatistics_tabs}>
          {PRODUCT_STATS_RANGE_TABS.map((tab) => (
            <div
              key={tab.value}
              className={cn(styles.productStatistics_tab, {
                [styles.productStatistics_tab__active]: range === tab.value
              })}
              onClick={() => setRange(tab.value)}
            >
              {t(tab.label)}
            </div>
          ))}
        </div>
        {range === ProductStatsRange.Custom && (
          <div className={styles.productStatistics_datepicker}>
            <StatisticChartDatePicker
              value={customRange}
              onChange={setCustomRange}
              activeRangeType={ChartRangeType.Custom}
              dateFormat={CHART_DATE_FORMATS_FOR_PICKER[ChartRangeType.Custom]}
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className={styles.productStatistics_loader}>
          <ClipLoader size={30} color="#fb3973" />
        </div>
      ) : data.length > 0 ? (
        <>
          <ProductLifetimeChart
            data={data}
            breakdownBy={breakdownBy}
            selectedIndex={selectedIndex}
            onSelectIndex={setSelectedIndex}
          />
          <ProductStatsByBarcodeTable
            point={selectedPoint}
            breakdownBy={breakdownBy}
          />
        </>
      ) : (
        <div className={styles.productStatistics_empty}>
          {t("No data for this period")}
        </div>
      )}
    </div>
  );
};

export default ProductStatistics;
